// src/input/gamepad.ts — Gamepad API → ham durum (WP6; DESIGN §2 standart eşleme: buttons 0–5, 9, 13; axes 0–1).
// Ölü bölge inputManager'da uygulanır (klavye+pad toplamı tek yoldan geçsin); burada yalnız ham eksen ve Btn maskesi.
// Taunt yalnız D-pad↓ (13); akor yok. Yerel çok oyuncuda her oyuncunun kendi GamepadSource'u vardır (localPlayers.ts):
// oyuncu → HAM yuva bağı sabittir ('exact' kipi), çünkü "ilk bağlı pad"e düşmek iki oyuncuyu aynı pad'e bağlardı.
// Mantık DOM'suz `readFrom(pad)` içinde (testler sahte pad nesnesi verir); `read(raw)` navigator.getGamepads() ile okur.
import { PAD_AXIS_X, PAD_AXIS_Y, PAD_BUTTONS } from '../config/controls.ts';
import type { InputSource, RawState } from './inputManager.ts';

/** Gamepad'in okuduğumuz alt kümesi (test sahteleri için yapısal tip). */
export interface GamepadLike {
  readonly buttons: ReadonlyArray<{ readonly pressed: boolean; readonly value: number }>;
  readonly axes: ReadonlyArray<number>;
  readonly mapping?: string;
}

/** Bir pad düğmesi basılı mı (analog tetik de sayılır). Menü/katılma tarayıcıları da bunu kullanır (tek doğru okuma). */
export function padButtonDown(pad: GamepadLike | null | undefined, i: number): boolean {
  if (!pad) return false;
  const b = pad.buttons[i];
  return b !== undefined && (b.pressed || b.value > 0.5);
}

/** Eksen değerini −1..1'e kırp; NaN → 0 (bazı sürücüler bağlantı anında NaN verir). */
const clampAxis = (v: number | undefined): number => (v === undefined || v !== v ? 0 : v < -1 ? -1 : v > 1 ? 1 : v);

/**
 * Pad seçim kipi — "hangi ham yuvayı okuyacağım" sorusunun üç yanıtı:
 *  • `'preferred'` (varsayılan, TEK OYUNCULU davranış): tercih edilen yuva bağlıysa o, değilse İLK bağlı pad.
 *    Pad 0 yerine 1. yuvaya düşse bile oyun çalışsın diye böyle; tek oyuncuda "bir pad varsa oynanır" kuralı.
 *  • `'slot'`: "N. bağlı pad" (ordinal). Menüde "kaçıncı pad" göstermek/eşlemek için.
 *  • `'exact'`: YALNIZ verilen ham yuva; başka pad'e DÜŞMEZ. Yerel çok oyuncuda zorunlu — düşme olsaydı P1'in pad'i
 *    koptuğunda P1, P2'nin pad'ini okumaya başlardı (iki oyuncu tek pad'i paylaşırdı).
 */
export type PadPick = 'preferred' | 'slot' | 'exact';

export class GamepadSource implements InputSource {
  /** Okunacak yuva: `pick` kipine göre ham yuva ('preferred' / 'exact') ya da "kaçıncı bağlı pad" ('slot'). */
  padIndex: number;
  /** Yuva çözümleme kipi (bkz. PadPick). Varsayılan 'preferred' — mevcut tek oyunculu davranış birebir korunur. */
  pick: PadPick;
  /** Son okumada pad bağlı mıydı (HUD "pad bağlandı" ipucu için). */
  connected = false;
  /** Son okumada GERÇEKTEN okunan yuva (tercih tutmazsa ilk bağlı pad); −1 = pad yok. Titreşim de bu yuvaya gider. */
  activeIndex = -1;
  private buttons = 0;
  private axisX = 0;
  private axisY = 0;

  constructor(padIndex = 0, pick: PadPick = 'preferred') { this.padIndex = padIndex; this.pick = pick; }

  /** Son okunan ham değerler (HUD / test). */
  get lastButtons(): number { return this.buttons; }
  get lastAxisX(): number { return this.axisX; }
  get lastAxisY(): number { return this.axisY; }

  /** DOM'suz: verilen pad'den Btn maskesi + ham eksenler. Analog tetik değeri de "pressed" sayılır (value > 0.5). */
  readFrom(pad: GamepadLike | null | undefined): void {
    if (!pad) { this.connected = false; this.buttons = 0; this.axisX = 0; this.axisY = 0; return; }
    this.connected = true;
    let mask = 0;
    const bs = pad.buttons;
    for (const key in PAD_BUTTONS) {
      const i = +key;
      const b = bs[i];
      if (b !== undefined && (b.pressed || b.value > 0.5)) mask |= PAD_BUTTONS[i]!;
    }
    this.buttons = mask;
    this.axisX = clampAxis(pad.axes[PAD_AXIS_X]);
    this.axisY = clampAxis(pad.axes[PAD_AXIS_Y]);
  }

  /** InputSource: Gamepad API'den oku (getGamepads tarayıcı tarafında dizi tahsis eder — kaçınılmaz, kare başı bir kez). */
  read(raw: RawState): void {
    if (typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function') {
      const pads = navigator.getGamepads();
      // Yuva SABİT DEĞİL: tarayıcı pad'i 0 dışında bir yuvaya koyabilir (önceki bağlantı, ikinci bir HID aygıtı, yeniden
      // bağlanma). Eskiden `pads[0]` okunuyordu ve pad 1. yuvadaysa MAÇTA hiçbir tuş çalışmıyordu; menü çalışıyordu
      // çünkü uiNav tüm yuvaları tarıyor. Artık ikisi de aynı çözümü kullanıyor: önce tercih edilen yuva, yoksa ilk bağlı pad.
      this.activeIndex = pickPadFor(pads, this.padIndex, this.pick);
      this.readFrom(this.activeIndex >= 0 ? pads[this.activeIndex] ?? null : null);
    } else {
      this.activeIndex = -1;
      this.readFrom(null);
    }
    raw.buttons |= this.buttons;
    raw.padAxisX += this.axisX;
    raw.padAxisY += this.axisY;
  }
}

/**
 * Kullanılacak pad yuvası: tercih edilen yuva bağlıysa o, değilse ilk bağlı pad; hiç yoksa −1.
 * (Yerel çok oyuncuda oyuncu i → yuva i tercihi; tek oyuncuda tercih tutmazsa ilk pad'e düşer.)
 */
export function resolvePadIndex(preferred = 0): number {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return -1;
  return pickPadIndex(navigator.getGamepads(), preferred);
}

/** `resolvePadIndex`in saf çekirdeği (DOM'suz, test edilir): dolu ilk uygun yuva. */
export function pickPadIndex(pads: ArrayLike<unknown>, preferred = 0): number {
  if (preferred >= 0 && preferred < pads.length && pads[preferred]) return preferred;
  for (let i = 0; i < pads.length; i++) if (pads[i]) return i;
  return -1;
}

/** Bir kaynağın kipine göre okunacak ham yuva (saf çekirdek; `read` bunu tek getGamepads çağrısıyla kullanır). */
export function pickPadFor(pads: ArrayLike<unknown>, padIndex: number, pick: PadPick): number {
  if (pick === 'preferred') return pickPadIndex(pads, padIndex);
  if (pick === 'slot') return pickPadSlot(pads, padIndex);
  // 'exact': düşme YOK — yuva boşsa pad yok sayılır (yerel çok oyuncuda pad kimliği sabit kalsın).
  return padIndex >= 0 && padIndex < pads.length && pads[padIndex] ? padIndex : -1;
}

/**
 * "N. bağlı pad"in ham yuvası (saf çekirdek). Ham yuvalar boşluklu olabilir: [0, 2] bağlıysa slot 0 → 0, slot 1 → 2.
 * Neden gerek: oyuncu "2. pad" der ama tarayıcı pad'i 2. yuvaya koymuş olabilir (önceki bağlantı, ikinci HID aygıtı).
 */
export function pickPadSlot(pads: ArrayLike<unknown>, slot: number): number {
  if (slot < 0) return -1;
  let n = 0;
  for (let i = 0; i < pads.length; i++) {
    if (!pads[i]) continue;
    if (n === slot) return i;
    n++;
  }
  return -1;
}

/** `pickPadSlot`in DOM'lu sarmalayıcısı: N. bağlı pad'in ham yuvası; yoksa −1. */
export function padSlotToIndex(slot: number): number {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return -1;
  return pickPadSlot(navigator.getGamepads(), slot);
}

/** Ters yön (saf): ham yuva → "kaçıncı bağlı pad"; yuva boşsa −1. Menüde "P2 = 2. pad" yazmak için. */
export function padSlotOfIndex(pads: ArrayLike<unknown>, index: number): number {
  if (index < 0 || index >= pads.length || !pads[index]) return -1;
  let n = 0;
  for (let i = 0; i < index; i++) if (pads[i]) n++;
  return n;
}

/** `padSlotOfIndex`in DOM'lu sarmalayıcısı. */
export function indexToPadSlot(index: number): number {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return -1;
  return padSlotOfIndex(navigator.getGamepads(), index);
}

/** Dolu ham yuvalar, artan sırada (saf). Dizi TAHSİS EDER — kare başı bir kez, sıcak yolda değil. */
export function padSlotsOf(pads: ArrayLike<unknown>): number[] {
  const out: number[] = [];
  for (let i = 0; i < pads.length; i++) if (pads[i]) out.push(i);
  return out;
}

/** Bağlı pad'lerin ham yuvaları, artan sırada (menü: kim katılabilir). DOM yoksa boş dizi. */
export function connectedPadSlots(): number[] {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return [];
  return padSlotsOf(navigator.getGamepads());
}

/** Tanılama (window.__pads): bağlı pad'lerin yuvası, kimliği, eşlemesi ve o an basılı tuş/eksenleri. */
export function padDiagnostics(): unknown[] {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return [];
  const out: unknown[] = [];
  const pads = navigator.getGamepads();
  for (let i = 0; i < pads.length; i++) {
    const p = pads[i];
    if (!p) continue;
    const pressed: number[] = [];
    for (let b = 0; b < p.buttons.length; b++) { const btn = p.buttons[b]; if (btn && (btn.pressed || btn.value > 0.5)) pressed.push(b); }
    out.push({ index: i, id: p.id, mapping: p.mapping, connected: p.connected, pressed, axes: p.axes.map((a) => +a.toFixed(2)) });
  }
  return out;
}

/** Bağlı pad sayısı (menü: "ileride yerel çok oyuncu" — oyuncu i → pad i). DOM yoksa 0. */
export function connectedPadCount(): number {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return 0;
  const pads = navigator.getGamepads();
  let n = 0;
  for (let i = 0; i < pads.length; i++) if (pads[i]) n++;
  return n;
}
